/**
 * O repertório da IA, por módulo — o que a grade do estado vazio oferece.
 *
 * Existe porque as quatro pílulas de texto que estavam ali não diziam DE ONDE a
 * resposta vem: quem abria a tela não descobria que há Consolidador, Comissões
 * ou Compliance do outro lado. A grade mostra o alcance em vez de descrevê-lo.
 *
 * ## Contrato de cada entrada
 *
 * | campo | para que serve |
 * |---|---|
 * | `moduleKey` | casa com o mapa do `destinoPorModulo()`. Aba sem chave lá não aparece. |
 * | `rotulo` | o nome do módulo. Ver o ⚠️ dos rótulos, abaixo. |
 * | `descricao` | o que o módulo cobre, ao lado do nome na linha do menu do "+". Uma frase. |
 * | `icone` | ícone DO MÓDULO, para a linha dele no menu do "+" (as ações têm o próprio). |
 * | `icone` | nome de arquivo em `components/icons/svg/`. Ver o ⚠️ abaixo. |
 * | `prompt` | vai INTEIRO para o chat. O clique envia, não preenche (mesmo gesto das pílulas). |
 *
 * ⚠️ **`icone` precisa existir em `components/icons/svg/`.** Nome inexistente
 * não quebra nada: o `icon-svg` renderiza vazio, sem aviso no console. Todos os
 * daqui foram conferidos contra a pasta um a um.
 *
 * ⚠️ **`moduleKey` é a chave do MENU, não a rota.** `carteira` para o
 * Consolidador (o destino é dinâmico), `task` para tarefas — `tarefas` é o
 * módulo LEGADO, e apontar para ele foi o defeito de 25/08 nos insights da
 * lateral. Quem resolve os dois casos é o `buildGestaoGroups`, via
 * `utils/destino-por-modulo.js`.
 *
 * ## ⚠️ O `rotulo` é o nome CANÔNICO do módulo na plataforma
 *
 * Não é texto livre: tem de bater com o que a nav-bar e a busca global chamam
 * o módulo, senão a mesma coisa aparece com dois nomes na mesma sessão. A
 * fonte é `modules/global/utils/global-search/sidebar-modules.js`.
 *
 * Dois já nasceram errados aqui e foram corrigidos em 27/08:
 *   "Planejamento Financeiro" → **Financial Planning**
 *   "Tarefas"                 → **Gerenciador de Tarefas** (o legado "Tarefas"
 *                                foi retirado do menu; o módulo ativo é este)
 *
 * ## ⚠️ Título curto: a ABA já diz o módulo
 *
 * Teto de 18 caracteres — é o que cabe numa linha na coluna mais estreita da
 * grade (148px menos os paddings, a 13px semibold). Acima disso o título quebra
 * em duas e os cartões da linha ficam com alturas diferentes, que foi o que
 * reprovou a primeira tentativa.
 *
 * O corte sai de graça porque o título NÃO precisa repetir o contexto: quem lê
 * "Por produto" está com a aba "Comissões" acesa logo acima, e "Desenquadradas"
 * está sob "Compliance". Era "Receita por produto" e "Carteiras desenquadradas"
 * — o módulo dito duas vezes na mesma tela. Quem carrega o detalhe é a
 * `descricao`, logo abaixo.
 *
 * ## ⚠️ Quantas ações por módulo — depende do LAYOUT
 *
 * No **carrossel** (o do menu do "+") não há teto: os cartões ficam numa linha
 * só que rola, e quatro cabem por vez. O quinto é justamente o que convida a
 * rolar. O CRM tem sete, um por cartão real do chat (`crm/ia/registry.js`) —
 * o suficiente para exercitar o scroll sem inventar pergunta sem tool atrás.
 *
 * Na **grade** (o layout de cartão quadrado, retirado do estado vazio da `/ia`
 * em 10/09/2026 — ver `views/chat-imersivo/constants.js`) o teto era quatro:
 * ali a coluna do estado vazio é `justify-center` e cada linha a mais empurra o
 * composer para cima. O layout atual do estado vazio é a LISTA fixa de
 * módulos (`unidade: 'modulo'` em `components/acoes-por-modulo/`), sem teto —
 * ela rola, não empurra.
 *
 * ## Sobre o `prompt` estar escrito à mão
 *
 * Estas são perguntas curadas, não uma varredura das tools. Uma tool não vira
 * boa pergunta sozinha: `list_oportunidades` responde "listei 12", enquanto
 * "quais oportunidades estão paradas há mais tempo" gera análise.
 *
 * [bug corrigido, 31/08] O CRM tinha oito ações, e três delas ("Sem contato",
 * "Alertas pendentes", "Fechados no mês"/"Motivos de perda") não correspondiam
 * a NENHUMA tool real — perguntas que soavam boas mas caíam num
 * `fallback-card` ou num raciocínio sem fonte. Agora o registry de UI do chat
 * já existe (`crm/ia/registry.js`, spec-010): cada ação abaixo tem UM cartão
 * real por trás, e a lista tem exatamente sete, uma por cartão
 * (`crm/funil`, `crm/funil-leads`, `crm/oportunidades`,
 * `crm/clientes-prioritarios`, `crm/atividades-sugeridas`, `crm/emails`,
 * `crm/conversas-whatsapp`). "Funil de leads" (`get_funil_leads`) entrou em
 * 31/08 assim que o cartão `crm/funil-leads` ficou pronto — Leads é pipeline
 * SEPARADO e ANTERIOR ao de oportunidades (lead converte em oportunidade),
 * com kanban próprio (`/crm/leads/kanban`).
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

export const ACOES_POR_MODULO = [
  {
    // [feedback do usuário, 10/09] Fee Fixo é a PRIMEIRA entrada do catálogo
    // de propósito: é o módulo que o pedido de voltar às sugestões fixas por
    // módulo (`views/chat-imersivo/constants.js`) nomeou explicitamente como
    // faltante. `descricao` é a MESMA frase do agente do catálogo
    // (`AGENTE_FEE_FIXO.description`, `constants/Agentes.js`) — as duas
    // descrevem o mesmo módulo, e divergir aqui faria a IA parecer dois
    // produtos diferentes dependendo de onde o profissional a encontra.
    moduleKey: 'fee-fixo',
    // Mesmo ícone da entrada de menu real (`ROUTE_DEFS[ROUTE_KEYS.FEE_FIXO]`,
    // `src/constants/navigation-routes.js`) — é o glifo que a sidebar e a
    // busca global já usam para este módulo.
    icone: 'mdi-chart-timeline-variant',
    rotulo: 'Fee Fixo',
    descricao: 'Migração de comissionado para fee fixo, cliente a cliente.',
    acoes: [
      {
        id: 'fee-fixo-simulacao',
        titulo: 'Simular migração de um cliente',
        descricao: 'Simula o custo do fee fixo para um cliente específico.',
        icone: 'arrow-swap-horizontal',
        prompt: 'Quero fazer simulação migração de fee fixo para um cliente',
        // tool: 'criar_simulacao_fee_fixo' — fee-fixo/simulacao-card
      },
      {
        id: 'fee-fixo-elegiveis',
        titulo: 'Quais clientes já posso migrar',
        descricao: 'Recorte de clientes priorizados para a migração.',
        icone: 'user-tag',
        prompt: 'Quais clientes já posso migrar para o fee fixo?',
        // tool: 'sugerir_filtro_clientes' — fee-fixo/recorte-card
      },
      {
        id: 'fee-fixo-relatorio',
        titulo: 'Enviar relatório de simulação',
        descricao: 'Gera e envia o relatório da simulação ao cliente.',
        icone: 'document-text',
        prompt:
          'Quero enviar o relatório da simulação de fee fixo para o cliente',
        // tool: 'preparar_envio_relatorio_fee_fixo' — fee-fixo/envio-relatorio
      },
    ],
  },
  {
    moduleKey: 'crm',
    icone: 'crm',
    rotulo: 'CRM',
    descricao: 'Pipeline, negócios e relacionamento com a carteira.',
    // [feedback do usuário, 31/08] ordem pedida: clientes prioritários → funil
    // de leads → WhatsApp → e-mails → as duas de oportunidades → atividades
    // sugeridas.
    acoes: [
      {
        id: 'crm-clientes-prioritarios',
        titulo: 'Quais são meus clientes prioritários hoje?',
        descricao: 'Fila da régua de relacionamento.',
        icone: 'user-tag',
        prompt: 'Quais são meus clientes prioritários hoje?',
        // tool: 'list_crm_clientes_prioridade_relacionamento' — crm/clientes-prioritarios
      },
      {
        id: 'crm-funil-leads',
        titulo: 'Como está o funil de leads?',
        descricao: 'Leads agrupados por etapa, com valor em jogo.',
        icone: 'status-search',
        prompt: 'Como está o funil de leads?',
        // tool: 'get_funil_leads' — crm/funil-leads (crm/ia/registry.js)
      },
      {
        id: 'crm-whatsapp',
        titulo: 'Quais conversas de WhatsApp tive recentemente?',
        descricao: 'Trocas recentes de WhatsApp.',
        icone: 'whatsapp',
        prompt: 'Quais conversas de WhatsApp tive recentemente com meus clientes?',
        // tool: 'list_conversas_whatsapp' — crm/conversas-whatsapp
      },
      {
        id: 'crm-emails',
        titulo: 'Quais e-mails recentes troquei com meus clientes?',
        descricao: 'Trocas recentes por e-mail.',
        // [feedback do usuário, 31/08] o ícone `email` tem um path com fill
        // fixo (#231F20), ignorando a prop `color` — renderiza um ícone
        // preto E colorido sobrepostos, desproporcional. `sms` é o MESMO
        // ícone que a pasta "Todos" usa em /crm/comunicacoes/emails
        // (`ICON_ALL` de `crm/constants/EmailIcons.js`) — cor dinâmica, e
        // já validado na tela real.
        icone: 'sms',
        prompt: 'Quais e-mails recentes troquei com meus clientes?',
        // tool: 'list_emails_crm' — crm/emails
      },
      {
        id: 'crm-funil-oportunidades',
        titulo: 'Como está o funil de oportunidades?',
        descricao: 'Etapas do pipeline, com valor em jogo.',
        icone: 'chart-grow',
        prompt: 'Como está o funil de oportunidades?',
        // tool: 'get_funil_vendas' — crm/funil (crm/ia/registry.js)
      },
      {
        id: 'crm-oportunidades-paradas',
        titulo: 'Quais oportunidades estão paradas há mais tempo?',
        descricao: 'Negócios sem avançar de etapa.',
        icone: 'timer-warning',
        prompt: 'Quais oportunidades estão paradas há mais tempo?',
        // tool: 'list_oportunidades' — crm/oportunidades
      },
      {
        id: 'crm-atividades-sugeridas',
        titulo: 'Quais atividades sugeridas eu tenho pendentes?',
        descricao: 'O que o CRM sugere fazer agora.',
        icone: 'calendar-check',
        prompt: 'Quais atividades sugeridas eu tenho pendentes?',
        // tool: 'list_crm_atividades_sugeridas' — crm/atividades-sugeridas
      },
    ],
  },
  {
    moduleKey: 'carteira',
    // [feedback do usuário, 31/08] mesmo ícone da navbar real da plataforma
    // (`ROUTE_DEFS[ROUTE_KEYS.CONSOLIDADOR].icon`, `constants/navigation-routes.js`)
    // — `wallet` não é o ícone do módulo em lugar nenhum da plataforma.
    icone: 'donut',
    rotulo: 'Consolidador',
    descricao: 'AuC, alocação, rentabilidade e concentração.',
    acoes: [
      {
        id: 'carteira-auc',
        titulo: 'AuC sob gestão',
        descricao: 'Patrimônio total e como ele se moveu no mês.',
        icone: 'moneys',
        prompt: 'Qual o AuC total da minha carteira e como variou neste mês?',
      },
      {
        id: 'carteira-alocacao',
        titulo: 'Alocação',
        descricao: 'Distribuição entre renda fixa, variável e alternativos.',
        icone: 'graph-pie',
        prompt: 'Como está a alocação da minha carteira por classe de ativo?',
      },
      {
        id: 'carteira-rentabilidade',
        titulo: 'Rentabilidade',
        descricao: 'Desempenho no mês e no ano contra o benchmark.',
        icone: 'line-up-graph',
        prompt:
          'Qual a rentabilidade da minha carteira no mês e no ano contra o CDI?',
      },
      {
        id: 'carteira-concentracao',
        titulo: 'Concentração',
        descricao: 'Clientes ou ativos com peso acima do confortável.',
        icone: 'warning-circle',
        prompt:
          'Existe concentração excessiva em algum ativo ou emissor na minha carteira?',
      },
    ],
  },
  {
    moduleKey: 'task',
    // [feedback do usuário, 31/08] mesmo ícone da navbar real da plataforma
    // (`ROUTE_DEFS[ROUTE_KEYS.TAREFAS].icon`, `constants/navigation-routes.js`).
    icone: 'calendar-check',
    rotulo: 'Gerenciador de Tarefas',
    descricao: 'Tarefas do dia, atrasos e a semana à frente.',
    acoes: [
      {
        id: 'task-hoje',
        titulo: 'O que fazer hoje',
        descricao: 'Suas tarefas do dia, na ordem que importa.',
        icone: 'task-square',
        prompt: 'O que eu preciso fazer hoje?',
      },
      {
        id: 'task-atrasadas',
        titulo: 'Tarefas atrasadas',
        descricao: 'O que passou do prazo e ainda está aberto.',
        icone: 'clock',
        prompt: 'Quais são minhas tarefas atrasadas?',
      },
      {
        id: 'task-semana',
        titulo: 'Semana à frente',
        descricao: 'O que vence nos próximos dias, por prioridade.',
        icone: 'calendar',
        prompt: 'O que vence nos próximos sete dias?',
      },
      {
        id: 'task-por-cliente',
        titulo: 'Por cliente',
        descricao: 'Quais clientes concentram mais tarefas abertas.',
        icone: 'profile-two-user',
        prompt: 'Quais clientes têm mais tarefas abertas na minha lista?',
      },
    ],
  },
  {
    moduleKey: 'fp',
    // [feedback do usuário, 31/08] mesmo ícone da navbar real da plataforma
    // (`ROUTE_DEFS[ROUTE_KEYS.FP_HOME].icon`, `constants/navigation-routes.js`).
    // `fp-briefcase` não é o ícone do módulo em lugar nenhum da plataforma —
    // `wallet` (visto antes no Consolidador) é o de OUTRO módulo (Finia).
    icone: 'mdi-chart-timeline-variant',
    rotulo: 'Financial Planning',
    descricao: 'Planos, metas, aportes e revisões pendentes.',
    acoes: [
      {
        id: 'fp-planos-ativos',
        titulo: 'Planos ativos',
        descricao: 'Quantos planos estão em andamento e em que estágio.',
        icone: 'fp-briefcase',
        prompt:
          'Quantos planejamentos financeiros estão ativos e em que estágio cada um está?',
      },
      {
        id: 'fp-metas-risco',
        titulo: 'Metas em risco',
        descricao: 'Objetivos que não vão ser atingidos no ritmo atual.',
        icone: 'status-up',
        prompt:
          'Quais metas de planejamento financeiro estão em risco de não serem atingidas?',
      },
      {
        id: 'fp-aportes',
        titulo: 'Aportes previstos',
        descricao: 'O que os planos preveem entrar nos próximos meses.',
        icone: 'money-send',
        prompt:
          'Quais aportes estão previstos nos planejamentos dos meus clientes?',
      },
      {
        id: 'fp-sem-revisao',
        titulo: 'Planos sem revisão',
        descricao: 'Quem está há tempo demais sem atualizar o plano.',
        icone: 'document-historic',
        prompt: 'Quais planejamentos estão há mais tempo sem revisão?',
      },
    ],
  },
  {
    moduleKey: 'comissoes',
    // [feedback do usuário, 31/08] mesmo ícone da navbar real da plataforma
    // (`ROUTE_DEFS[ROUTE_KEYS.COMISSOES].icon`, `constants/navigation-routes.js`).
    icone: 'coin',
    rotulo: 'Comissões',
    descricao: 'Fechamento, receita por produto e RoA.',
    acoes: [
      {
        id: 'comissoes-fechamento',
        titulo: 'Fechamento do mês',
        descricao: 'Quanto fechou e como se compara ao mês anterior.',
        icone: 'usd-circle',
        prompt:
          'Qual o valor do fechamento de comissões deste mês e a variação contra o anterior?',
      },
      {
        id: 'comissoes-por-produto',
        titulo: 'Por produto',
        descricao: 'De onde a comissão veio, por linha de produto.',
        icone: 'chart-bar',
        prompt: 'Como minha receita de comissões se divide por produto?',
      },
      {
        id: 'comissoes-pendentes',
        titulo: 'A receber',
        descricao: 'O que foi apurado e ainda não caiu.',
        icone: 'card-tick',
        prompt:
          'Quais comissões estão apuradas e ainda pendentes de pagamento?',
      },
      {
        id: 'comissoes-roa',
        titulo: 'RoA da carteira',
        descricao: 'Retorno sobre ativos, e como ele evoluiu.',
        icone: 'trend-up',
        prompt:
          'Qual o RoA da minha carteira e como ele evoluiu nos últimos meses?',
      },
    ],
  },
  {
    moduleKey: 'compliance',
    // [feedback do usuário, 31/08] mesmo ícone da navbar real da plataforma
    // (`ROUTE_DEFS[ROUTE_KEYS.COMPLIANCE].icon`, `constants/navigation-routes.js`).
    icone: 'book-cover',
    rotulo: 'Compliance',
    descricao: 'Suitability, desenquadramento e documentos.',
    acoes: [
      {
        id: 'compliance-pendencias',
        titulo: 'Clientes pendentes',
        descricao: 'Quem tem cadastro ou documento em aberto.',
        icone: 'campos-obrigatorios',
        prompt: 'Quais clientes estão com pendências de compliance?',
      },
      {
        id: 'compliance-suitability',
        titulo: 'Suitability',
        descricao: 'Perfis de investidor fora do prazo de validade.',
        icone: 'security-time',
        prompt: 'Quais clientes estão com o perfil de suitability vencido?',
      },
      {
        id: 'compliance-desenquadrados',
        titulo: 'Desenquadradas',
        descricao: 'Alocação fora do perfil declarado pelo cliente.',
        icone: 'warning-status',
        prompt:
          'Quais clientes estão com a carteira desenquadrada do perfil declarado?',
      },
      {
        id: 'compliance-documentos',
        titulo: 'Documentos',
        descricao: 'O que expira e precisa ser renovado.',
        icone: 'document-text',
        prompt: 'Quais documentos de clientes vencem nos próximos 60 dias?',
      },
    ],
  },
]

/**
 * A chave do "módulo" dos atalhos dentro de `acoesPorModulo`.
 *
 * Exportada porque `view-chat.vue` precisa RECONHECER esse módulo na lista
 * que recebe por prop, para saber quais ações podem ser digitadas como
 * comando (G-2). Dois literais `'atalhos-de-negocio'` em arquivos diferentes
 * divergiriam calados: o sintoma seria o atalho digitado voltar a sair como
 * pergunta livre.
 */
export const MODULE_KEY_ATALHOS_DE_NEGOCIO = 'atalhos-de-negocio'

/**
 * O "módulo" dos ATALHOS de negócio — spec-001
 * `project-home-ia-skills/preparacao-reuniao`, [T018], US-1.
 *
 * A home-ia lista o que `GET /v2/api/ia-crm/atalhos` devolveu
 * (`api/apiAtalhos.js`), na MESMA lista das ações por módulo. **O filtro é do
 * SERVIDOR**: a resposta já vem filtrada por papel e escritório
 * (`core/atalhos/catalogo.py::catalogo_para`) — esta função só TRADUZ o shape
 * do atalho para o shape de `ação` que `acoes-por-modulo.vue` já sabe
 * desenhar. Ela **não** reimplementa RBAC nenhum: usuário sem acesso a um
 * atalho simplesmente não aparece no array que o backend devolveu, e por
 * isso não aparece aqui.
 *
 * ⚠️ **O clique de uma ação de atalho NÃO manda texto livre.** O atalho só é
 * alcançado pelo campo estruturado `comando`
 * (`core/atalhos/preflight.py:181-205`: "não há parsing de texto
 * digitado") — por isso cada ação carrega `comando: {slug}` em vez de
 * `prompt`, e `acoes-por-modulo.vue::usar()` distingue os dois (`comando` →
 * evento `usar-comando`; `prompt` → evento `use`, como sempre).
 *
 * ★ [G-2 do QA, 19/09/2026] Isto NÃO impede o atalho DIGITADO
 * (`/pré-reunião Ilo Santos`): quem traduz o texto para o mesmo `comando`
 * estruturado é o FRONT (`utils/atalho-digitado.js`, consumido por
 * `view-chat.vue::onSendMessage`). O servidor continua sem parsear `query` —
 * o que muda é que a tela passou a saber montar o comando a partir do que o
 * consultor escreveu, que é o que o próprio servidor manda ele fazer quando
 * falta o cliente.
 *
 * `null` com a lista vazia (escritório sem nenhum atalho visível, ou
 * catálogo que ainda não respondeu) — é o que faz o bloco inteiro não
 * aparecer, em vez de uma aba "Atalhos" vazia.
 *
 * @param {Array} atalhos O `value` de `apiAtalhos.listar()` —
 *   `[{id, label, description, pedeArgumento, placeholderArgumento}]`
 *   (contrato REAL do backend — ver o cabeçalho de `api/apiAtalhos.js`).
 * @returns {Object|null}
 */
export function moduloDeAtalhosDeNegocio(atalhos) {
  const lista = Array.isArray(atalhos) ? atalhos : []
  if (!lista.length) return null

  return {
    moduleKey: MODULE_KEY_ATALHOS_DE_NEGOCIO,
    icone: 'flash',
    rotulo: 'Atalhos',
    descricao: 'Comandos prontos que a IA já sabe executar.',
    acoes: lista
      .filter(function (atalho) {
        return atalho && atalho.id
      })
      .map(function (atalho) {
        return {
          id: 'atalho-' + atalho.id,
          titulo: atalho.label || atalho.id,
          descricao: atalho.description || '',
          icone: 'flash',
          comando: {
            slug: atalho.id,
            pedeArgumento: Boolean(atalho.pedeArgumento),
          },
        }
      }),
  }
}
